import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { FormNumberInput } from "@/components/shared/form/form-number-input";
import { SelectInput } from "@/components/shared/form/select-input";
import { ValidationErrorMessage } from "@/components/shared/form/shared/form-validation";
import { SupplierDropdownInput } from "@/components/shared/form/supplier-dropdown-input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Form } from "@/components/ui/form";
import { TypographyLarge } from "@/components/ui/typography";
import { currencyCodes } from "@/lib/constants/constants";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import { useUpdateInvoice } from "@/queries/invoices/update";
import { useGetAllSuppliers } from "@/queries/suppliers/get-all";
import { TransferNeededSelect } from "./transfer-needed-select";

const invoiceFormSchema = z.object({
  invoiceId: z.string(),
  invoiceNo: z.string().optional().nullable(),
  supplierId: z.string().optional().nullable(),
  totalAmount: z.number().optional().nullable(),
  currency: z.string().optional().nullable(),
  invoiceNoConfidence: z.number().optional().nullable(),
  totalAmountConfidence: z.number().optional().nullable(),
  supplierMatchConfidence: z.number().optional().nullable(),
  dueDateConfidence: z.number().optional().nullable(),
  isPaid: z.boolean().optional().nullable(),
});

type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

type Props = {
  values: InvoiceFormValues;
  disabled?: React.ReactNode;
  onFieldFocus: (field: any) => void;
  onFieldBlur: () => void;
  onValuesChange?: (values: InvoiceFormValues) => void;
  onPaidChange?: (value: boolean) => void;
};

const _InvoiceForm = ({
  values,
  disabled = false,
  onFieldFocus,
  onFieldBlur,
  onValuesChange,
  onPaidChange,
}: Props) => {
  const { t } = useTranslation();
  const { data: suppliers } = useGetAllSuppliers();
  const { companySlug } = useTenant();
  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    values,
  });

  const [updateInvoice] = useUpdateInvoice();

  const handleSubmit = (currentValues: InvoiceFormValues) => {
    const changedFields = Object.keys(currentValues).reduce(
      (acc, key) => {
        const typedKey = key as keyof InvoiceFormValues;
        const currentValue = currentValues[typedKey];
        const initialValue = values[typedKey];

        if (currentValue === null && initialValue === null) return acc;
        if (currentValue == null || initialValue == null) {
          (acc as any)[typedKey] = currentValue;
          return acc;
        }

        if (currentValue !== initialValue) {
          (acc as any)[typedKey] = currentValue;
        }
        return acc;
      },
      {} as Partial<InvoiceFormValues>,
    );

    const _values = {
      invoiceId: values.invoiceId,
      ...changedFields,
    };

    updateInvoice(_values);
  };

  const formValues = form.watch();

  const currencyOptions = useMemo(
    () =>
      currencyCodes.map((currency) => {
        return {
          label: currency,
          value: currency,
        };
      }),
    [],
  );

  useEffect(() => {
    onValuesChange?.(formValues);
  }, [formValues, onValuesChange]);

  const handlePaidChange = (value: boolean) => {
    onPaidChange?.(value);
  };

  return (
    <Form {...form}>
      <form className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <TypographyLarge>{t("invoice_details.title")}</TypographyLarge>
        </div>
        <div className="col-span-2 flex flex-col justify-center">
          <div className="flex flex-row flex-1 justify-center gap-2">
            <div className="flex-1">
              <SupplierDropdownInput
                label={t("vendor")}
                name="supplierId"
                options={suppliers}
                className={cn(
                  "flex-1",
                  confColor(values.supplierMatchConfidence),
                )}
                enableClear={false}
                readOnly={disabled}
              />
            </div>
            <div className="flex flex-col items-center self-end">
              <Link
                to="/$company/unternehmen/einstellungen/kreditoren"
                params={{ company: companySlug }}
                search={{ focusedRowId: formValues.supplierId } as any}
                className="text-sm text-foreground flex items-center gap-2 px-2 py-1 rounded hover:bg-muted h-11"
              >
                <ExternalLink className="h-4 w-4" />
                {t("edit_supplier")}
              </Link>
            </div>
          </div>
          <ValidationErrorMessage name="supplierId" />
        </div>
        <div className="col-span-2">
          <div className="flex flex-row gap-1 items-end w-full">
            <div className="flex flex-col items-start flex-1">
              <FormNumberInput
                label={t("total_amount")}
                name="totalAmount"
                className={cn(
                  "flex-1",
                  confColor(values.totalAmountConfidence),
                )}
                onFocus={() => onFieldFocus("totalAmount")}
                onBlur={onFieldBlur}
                disableFastCentInput
                readOnly={disabled}
              />
              <ValidationErrorMessage name="totalAmount" />
            </div>
            <div className="self-start">
              <SelectInput
                label={t("currency")}
                name="currency"
                options={currencyOptions}
                className={cn(
                  confColor(
                    values.totalAmountConfidence && values.currency
                      ? 1
                      : values.totalAmountConfidence === 0
                        ? 0
                        : undefined,
                  ),
                  "w-full",
                )}
                readOnly={disabled}
              />
              <ValidationErrorMessage name="currency" />
            </div>
          </div>
        </div>
        <div className="col-span-2 mt-4">
          <TypographyLarge>{t("payment_details.status_title")}</TypographyLarge>
        </div>
        <div className="col-span-2 mb-2">
          <TransferNeededSelect
            value={values.isPaid ?? false}
            onChange={handlePaidChange}
          />
        </div>
        <AutoSaveForm onSubmit={handleSubmit} immediate />
      </form>
    </Form>
  );
};

export const InvoiceForm = memo(_InvoiceForm);
